import React from 'react';
import {Menu} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CiteQuickMenu from './DocumentContextMenus/CiteQuickMenu';
import SelectPassageDialog from './DocumentContextMenus/SelectPassageDialog';
import SourcePackageQuickMenu from './DocumentContextMenus/SourcePackageQuickMenu';
import SourcePackageSubmenu from './DocumentContextMenus/SourcePackageSubmenu';
import CreateFolderDialog from './DocumentContextMenus/CreateFolderDialog';
import SubmenuMenuItem from './DocumentContextMenus/SubmenuMenuItem';
import {useFolderCreation} from './DocumentContextMenus/useFolderCreation';
import {useYSourcePackage} from './sourcePackage/hooks';

// Presents the main context menu and the nested Cite submenu.
// Props:
// - menuPosition: { mouseX, mouseY } | null
// - onClose: function
// - citeAnchorEl: HTMLElement | null
// - setCiteAnchorEl: function
// - fragmentMarks: Array<{ uid: string, text?: string }>
// - selectedFragmentUid: string | null
// - selectedFragmentMark: { uid: string, text?: string } | null
// - onCiteToFragment: (uid: string) => void
// - onAddToSourceRoot: () => void
// - onAddToSourceFolder: (folderId: string) => void
// - onAddToSourceNewFolder: (name?: string) => (string|Promise<string>|void|Promise<void>)
export default function DocumentContextMenus({
                                                 menuPosition,
                                                 onClose,
                                                 citeAnchorEl,
                                                 setCiteAnchorEl,
                                                 fragmentMarks,
                                                 selectedFragmentUid,
                                                 selectedFragmentMark,
                                                 onCiteToFragment,
                                                 onAddToSourceRoot,
                                                 onAddToSourceFolder,
                                                 onAddToAnotherProduct,
                                             }) {
    // Local anchors for submenus
    const [spAnchorEl, setSpAnchorEl] = React.useState(null);
    const [spDialogOpen, setSpDialogOpen] = React.useState(false);
    const [citeDialogOpen, setCiteDialogOpen] = React.useState(false);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [newFolderParentId, setNewFolderParentId] = React.useState(null);
    const [newFolderProductId, setNewFolderProductId] = React.useState(null);
    const [folderRefreshTrigger, setFolderRefreshTrigger] = React.useState(0);

    // Read folders from Yjs (real-time, no Zustand)
    const {folders: folderList} = useYSourcePackage();

    // Folder creation logic
    const {createFolder, error, setError} = useFolderCreation(onAddToSourceFolder, onClose);

    const handleCloseAll = () => {
        setSpAnchorEl(null);
        setCiteAnchorEl?.(null);
        onClose?.();
    };

    // Open cite submenu on hover
    const handleOpenCite = (e) => {
        setCiteAnchorEl?.(e.currentTarget);
        setSpAnchorEl(null);
    };

    // Open source package quick menu on hover
    const handleOpenSp = (e) => {
        setSpAnchorEl(e.currentTarget);
        setCiteAnchorEl?.(null);
    };

    // Open SelectPassageDialog when "Select passage..." is clicked
    const handleOpenSelectPassage = () => {
        setCiteDialogOpen(true);
        handleCloseAll();
    };

    // Open full dialog when "Choose another" is clicked
    const handleChooseAnother = () => {
        setSpDialogOpen(true);
        handleCloseAll();
    };

    // Handle new folder creation request with parent folder ID and optional product ID
    const handleCreateNewFolder = (parentFolderId, productId = null) => {
        setNewFolderParentId(parentFolderId);
        setNewFolderProductId(productId);
        setIsDialogOpen(true);
    };

    // Wrap createFolder to pass parent folder ID and product ID
    const handleFolderCreate = async (name) => {
        const result = await createFolder(name, newFolderParentId, newFolderProductId);
        // Trigger refresh of folder list in SourcePackageSubmenu
        setFolderRefreshTrigger(prev => prev + 1);
        return result;
    };

    const openMain = !!menuPosition;
    const openCite = !!citeAnchorEl && openMain;
    const openSp = !!spAnchorEl && openMain;

    // Popper modifiers to ensure submenu never overlaps the main menu
    const submenuModifiers = [
        {name: 'offset', options: {offset: [8, -4]}},
        {name: 'flip', enabled: false},
        {name: 'preventOverflow', options: {altAxis: true, tether: false, boundary: 'viewport'}},
    ];

    return (
        <>
            <Menu
                open={openMain}
                onClose={handleCloseAll}
                anchorReference="anchorPosition"
                anchorPosition={menuPosition ? {top: menuPosition.mouseY, left: menuPosition.mouseX} : undefined}
                slotProps={{paper: {className: 'evidence-allow-select'}}}
                autoFocus={false}
            >
                <SubmenuMenuItem
                    label="Cite for"
                    open={openCite}
                    onMouseEnter={handleOpenCite}
                />

                <SubmenuMenuItem
                    label="Add to source package"
                    open={openSp}
                    onMouseEnter={handleOpenSp}
                />
            </Menu>

            <CiteQuickMenu
                open={openCite}
                anchorEl={citeAnchorEl}
                fragmentMarks={fragmentMarks}
                selectedFragmentUid={selectedFragmentUid}
                selectedFragmentMark={selectedFragmentMark}
                onCiteToFragment={onCiteToFragment}
                onOpenSelectPassage={handleOpenSelectPassage}
                onClose={() => setCiteAnchorEl?.(null)}
                submenuModifiers={submenuModifiers}
            />

            {/* Quick hover submenu with suggested folders */}
            <SourcePackageQuickMenu
                open={openSp}
                anchorEl={spAnchorEl}
                folderList={folderList}
                onAddToRoot={onAddToSourceRoot}
                onAddToFolder={onAddToSourceFolder}
                onChooseAnother={handleChooseAnother}
                onClose={() => setSpAnchorEl(null)}
                submenuModifiers={submenuModifiers}
            />

            {/* Full dialog for browsing all folders */}
            <SourcePackageSubmenu
                open={spDialogOpen}
                folderList={folderList}
                onAddToRoot={onAddToSourceRoot}
                onAddToFolder={onAddToSourceFolder}
                onAddToAnotherProduct={onAddToAnotherProduct}
                onCreateNewFolder={handleCreateNewFolder}
                onClose={() => setSpDialogOpen(false)}
                folderRefreshTrigger={folderRefreshTrigger}
            />

            {/* Full dialog for selecting passage to cite */}
            <SelectPassageDialog
                open={citeDialogOpen}
                fragmentMarks={fragmentMarks}
                selectedFragmentUid={selectedFragmentUid}
                onCiteToFragment={onCiteToFragment}
                onClose={() => setCiteDialogOpen(false)}
            />

            <CreateFolderDialog
                open={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                    setNewFolderParentId(null);
                    setNewFolderProductId(null);
                }}
                onCreateFolder={handleFolderCreate}
            />

            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
                <Alert severity="error" onClose={() => setError(null)} sx={{width: '100%'}}>
                    {String(error)}
                </Alert>
            </Snackbar>
        </>
    );
}
